import { 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  MousePointer, 
  Percent, 
  DollarSign,
  AlertTriangle,
  ChevronRight
} from 'lucide-react'
import { Card, CardHeader, CardBody } from '../components/ui'
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts'

// Mock data
const kpiData = [
  { 
    title: 'インプレッション', 
    value: '1,234,567', 
    change: 12.3, 
    icon: Eye,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100'
  },
  { 
    title: 'クリック', 
    value: '45,678', 
    change: 8.5, 
    icon: MousePointer,
    color: 'text-green-600',
    bgColor: 'bg-green-100'
  },
  { 
    title: 'CTR', 
    value: '3.70%', 
    change: -0.2, 
    icon: Percent,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100'
  },
  { 
    title: '売上', 
    value: '¥1,234,567', 
    change: 15.2, 
    icon: DollarSign,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100'
  },
]

const chartData = [
  { date: '12/1', impressions: 45000, clicks: 1800 },
  { date: '12/2', impressions: 52000, clicks: 2100 },
  { date: '12/3', impressions: 48000, clicks: 1900 },
  { date: '12/4', impressions: 61000, clicks: 2400 },
  { date: '12/5', impressions: 55000, clicks: 2200 },
  { date: '12/6', impressions: 67000, clicks: 2800 },
  { date: '12/7', impressions: 72000, clicks: 3000 },
]

const campaignRanking = [
  { name: '春のゲーム祭り', impressions: 45000 },
  { name: '新作タイトルA', impressions: 38000 },
  { name: 'カフェキャンペーン', impressions: 32000 },
  { name: 'ドリンク割引', impressions: 28000 },
  { name: '会員募集', impressions: 25000 },
]

const alerts = [
  { type: 'warning', message: 'キャンペーンA 予算90%消化', time: '10分前' },
  { type: 'info', message: 'クリエイティブ審査待ち 3件', time: '30分前' },
  { type: 'error', message: '店舗B 配信エラー発生', time: '1時間前' },
]

export default function Dashboard() {
  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ダッシュボード</h1>
          <p className="text-sm text-gray-500 mt-1">広告配信状況の概要</p>
        </div>
        <select className="form-select w-auto">
          <option value="today">今日</option>
          <option value="yesterday">昨日</option>
          <option value="7days">過去7日間</option>
          <option value="30days">過去30日間</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi) => (
          <Card key={kpi.title}>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{kpi.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{kpi.value}</p>
                  <div className="flex items-center mt-2">
                    {kpi.change > 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                    )}
                    <span className={kpi.change > 0 ? 'text-green-600' : 'text-red-600'}>
                      {kpi.change > 0 ? '+' : ''}{kpi.change}%
                    </span>
                    <span className="text-gray-400 text-xs ml-1">vs 前日</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${kpi.bgColor}`}>
                  <kpi.icon className={`w-6 h-6 ${kpi.color}`} />
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">配信推移</h3>
          </CardHeader>
          <CardBody>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="impressions" 
                    stroke="#3b82f6" 
                    fillOpacity={1} 
                    fill="url(#colorImpressions)" 
                    name="インプレッション"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        {/* Campaign Ranking */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">キャンペーンランキング</h3>
          </CardHeader>
          <CardBody className="p-0">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={campaignRanking} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="#9ca3af" 
                    fontSize={11}
                    width={100}
                    tick={{ fill: '#374151' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="impressions" fill="#3b82f6" radius={[0, 4, 4, 0]} name="インプレッション" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Performance */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">店舗別パフォーマンス</h3>
            <button className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
              もっと見る <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              {[
                { name: '東京エリア', percentage: 45, color: 'bg-blue-500' },
                { name: '大阪エリア', percentage: 30, color: 'bg-green-500' },
                { name: '名古屋エリア', percentage: 15, color: 'bg-yellow-500' },
                { name: 'その他', percentage: 10, color: 'bg-gray-400' },
              ].map((region) => (
                <div key={region.name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">{region.name}</span>
                    <span className="font-medium text-gray-900">{region.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className={`${region.color} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${region.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Alerts */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">アラート</h3>
            <button className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
              すべて見る <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </CardHeader>
          <CardBody className="p-0">
            <div className="divide-y divide-gray-100">
              {alerts.map((alert, index) => (
                <div key={index} className="flex items-start p-4 hover:bg-gray-50 transition-colors">
                  <div className={`
                    p-2 rounded-lg mr-3
                    ${alert.type === 'warning' ? 'bg-yellow-100' : ''}
                    ${alert.type === 'info' ? 'bg-blue-100' : ''}
                    ${alert.type === 'error' ? 'bg-red-100' : ''}
                  `}>
                    <AlertTriangle className={`
                      w-4 h-4
                      ${alert.type === 'warning' ? 'text-yellow-600' : ''}
                      ${alert.type === 'info' ? 'text-blue-600' : ''}
                      ${alert.type === 'error' ? 'text-red-600' : ''}
                    `} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{alert.message}</p>
                    <p className="text-xs text-gray-500 mt-1">{alert.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

